import React, { Fragment, useMemo } from "react";
import { Button } from "react-bootstrap";
import BTable from "react-bootstrap/Table";
import { useTable, useExpanded, useSortBy } from "react-table";
import styled from "styled-components";

import { themeColors } from "../../helpers/themeColors";
import AccordionDown from "../../assets/img/icons/accordion-down.svg";
import AccordionUp from "../../assets/img/icons/accordion-up.svg";
import SortedDesc from "../../assets/img/icons/table-sorted-desc.svg";
import SortedAsc from "../../assets/img/icons/table-sorted-asc.svg";
import { amountToString, joinClasses } from "../../helpers/utils";
import { kebabCase } from "lodash";
import { pricingBreakdown } from "../../constants/pricing";

const colors = themeColors();

const RatesTableStyled = styled(BTable)`
  margin-bottom: 33px;
  width: 100%;
  thead {
    th {
      font-weight: bold;
      font-size: 16px;
      line-height: 24px;
      letter-spacing: 5px;
      text-transform: uppercase;
      color: ${colors.white};
      background: transparent;
      border-top: 0 none;
      border-bottom-color: ${colors.mediumGray};
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      &:first-of-type {
        padding-left: 0;
      }
    }
  }
  tbody {
    td {
      font-weight: 700;
      font-size: 16px;
      line-height: 24px;
      padding-left: 0.25rem;
      padding-right: 0.25rem;
      @media screen and (min-width: 768px) {
        font-size: 20px;
        line-height: 28px;
      }
      letter-spacing: 0.15px;
      border-top-color: ${colors.mediumGray};
      &:first-of-type {
        padding-left: 0;
      }
      .expander-icon {
        @media screen and (min-width: 768px) {
          padding-left: 16px;
          padding-right: 16px;
        }
      }
      .charge {
        font-size: 16px;
        line-height: 24px;
        letter-spacing: 0.2px;
        font-weight: normal;
        @media screen and (min-width: 768px) {
          font-size: 18px;
          line-height: 24px;
        }
      }
      &.charge-name {
        width: 100px;
        @media screen and (min-width: 768px) {
          width: 148px;
        }
      }
      &.charge-expander {
        width: 50px;
        @media screen and (min-width: 768px) {
          width: 80px;
        }
      }
    }
    tr:last-of-type {
      td {
        border-bottom: 1px solid ${colors.mediumGray};
      }
    }
  }
`;

const ChargeButton = styled(Button)`
  max-width: 100px;
  width: 100%;
  @media screen and (min-width: 768px) {
    max-width: 148px;
  }
`;

const defaultPropGetter = () => ({});

const Table = ({
  columns,
  data,
  getHeaderProps = defaultPropGetter,
  getColumnProps = defaultPropGetter,
  getRowProps = defaultPropGetter,
  getCellProps = defaultPropGetter,
}) => {
  // Use the state and functions returned from useTable to build your UI
  const { getTableProps, headerGroups, rows, prepareRow } = useTable(
    {
      columns,
      data,
    },
    useSortBy,
    useExpanded
  );

  // Render the UI for your table
  return (
    <RatesTableStyled
      id="rates-table"
      className="text-white"
      {...getTableProps()}
    >
      <thead>
        {headerGroups.map((headerGroup) => (
          <tr {...headerGroup.getHeaderGroupProps()}>
            {headerGroup.headers.map((column) => {
              return (
                column.id !== "expander" && (
                  <th
                    {...column.getHeaderProps([
                      column.getSortByToggleProps(),
                      {
                        className: column.className,
                        style: column.style,
                      },
                      getColumnProps(column),
                      getHeaderProps(column),
                    ])}
                    colSpan={column.id === "total" ? 2 : 1}
                    // className="py-16px"
                  >
                    {column.render("Header")}
                    {column.isSorted && (
                      <img
                        className="align-baseline ml-3"
                        src={column.isSortedDesc ? SortedDesc : SortedAsc}
                        alt="Sort"
                      />
                    )}
                  </th>
                )
              );
            })}
          </tr>
        ))}
      </thead>
      <tbody>
        {rows.map((row, i) => {
          prepareRow(row);
          return (
            <Fragment key={row.original.id}>
              <tr>
                {row.cells.map((cell) => {
                  return (
                    <td
                      className="py-4"
                      {...cell.getCellProps([
                        {
                          className: cell.column.className,
                          style: cell.column.style,
                        },
                        getColumnProps(cell.column),
                        getCellProps(cell),
                      ])}
                    >
                      {cell.render("Cell")}
                    </td>
                  );
                })}
              </tr>
              {row.isExpanded && (
                <tr>
                  <td className="pb-4 pt-0 border-top-0" />
                  <td className="pb-4 pt-0 border-top-0">
                    <ul className="list-unstyled mb-0">
                      {row.original.charges.map((charge) => (
                        <li
                          key={`${row.original.id}-${charge.name}`}
                          className={joinClasses([
                            "charge mb-8px",
                            charge.name === "Total Charge" ? "bolder" : "",
                          ])}
                        >
                          {charge.name}
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="pb-4 pt-0 border-top-0">
                    <ul className="list-unstyled mb-0">
                      {row.original.charges.map((charge) => (
                        <li
                          key={`${row.original.id}-${charge.name}-charge`}
                          className={joinClasses([
                            "charge text-right mb-8px",
                            charge.name === "Total Charge" ? "bold" : "",
                          ])}
                        >{`${amountToString(charge.charge)}`}</li>
                      ))}
                    </ul>
                  </td>
                  <td className="px-0 pb-4 pt-0 border-top-0" />
                </tr>
              )}
            </Fragment>
          );
        })}
      </tbody>
    </RatesTableStyled>
  );
};

const RatesTable = ({ rates, shownRate }) => {
  const columns = useMemo(
    () => [
      {
        Header: "Rate",
        accessor: "name", // accessor is the "key" in the data
      },
      {
        Header: "Type",
        accessor: "rateType",
        className: "rate-type",
      },
      {
        Header: "Total Charge",
        accessor: "total",
        className: "charge-name",
        Cell: ({ cell: { value }, row }) => {
          const variant = shownRate === "express-rate" ? "primary" : "light";
          return (
            <ChargeButton
              variant={variant}
              className={shownRate === "ground-rate" ? "text-primary" : ""}
              size="lg"
              block
            >
              {`${amountToString(value)}`}
            </ChargeButton>
          );
        },
      },
      {
        Header: () => null,
        accessor: "expander",
        className: "charge-expander",
        Cell: ({ row }) => {
          return (
            <div
              {...row.getToggleRowExpandedProps()}
              title={`Toggle ${row.original.rateType}`}
              className="text-center expander-icon"
            >
              <img
                src={row.isExpanded ? AccordionUp : AccordionDown}
                alt={`Toggle ${row.original.rateType}`}
                className="mx-auto"
              />
            </div>
          );
        },
      },
    ],
    [shownRate]
  );

  const data = useMemo(() => {
    const formattedRates = rates.map((rate) => ({
      id: kebabCase(rate["ShippingType"]),
      name: shownRate === "express-rate" ? "Express Rate" : "Ground Rate",
      rateType: rate["ShippingType"],
      total: parseFloat(rate["total_charge"] || "0"),
      charges: pricingBreakdown.map((item) => ({
        name: item.name,
        charge: rate[item.value],
      })),
    }));

    return formattedRates;
  }, [rates, shownRate]);

  return (
    <div>
      <Table columns={columns} data={data} />
    </div>
  );
};

export default RatesTable;
